import * as esprima from 'esprima';
import { evalDistroConditional } from './eval';
import { AIAnalysis, SingleLLMAnalysis } from './survey';
import { sanitizeDangerousText } from './utils/textSanitizer';

export const FIELD_REF_REGEX = /\$[a-zA-Z\\_][a-zA-Z\\_0-9]+|\$\{.*\}/g;

export type Exact<TExpected, TActual extends TExpected> = TExpected extends TActual ? TExpected : never;

export function getFormulaParents(formula: string) {

  const masters = ['info', 'screener', 'org', 'view_info', 'provided'];

  const parents: string[] = [];
  let varUpNext: string | undefined;

  // if (!formula) throw new Error("Formula for " + key + " is missing!");

  let tokens;
  try {
    tokens = esprima.tokenize(formula);
  } catch (e) {
    console.log("Error tokenizing formula:", formula);
    throw e;
  }
  for (const token of tokens) {
    const foundVariable = token.value;
    if (varUpNext && masters.includes(varUpNext) && token.type === 'Punctuator' && foundVariable === '[') {
      varUpNext = 'String'
    }
    if (varUpNext === 'String' && token.type === 'String') {
      const fixedVariable = foundVariable.replace(/[\'\"]/g, '');
      parents.push(fixedVariable);

      varUpNext = undefined;
    }
    if (token.type === 'Identifier') {
      if (varUpNext) {
        if (varUpNext === 'info' || varUpNext === 'provided') {
          parents.push(foundVariable);
        }
        varUpNext = undefined;
      } else {
        if (masters.indexOf(foundVariable) !== -1) {
          varUpNext = foundVariable;
        }
      }
    }
  }

  return parents;
}

export function safeGetFormulaParents(formula: string) {
  try {
    return getFormulaParents(formula);
  } catch (e) {
    console.log("Error getting formula parents:", formula, e);
    return [];
  }
}

/** Gets all possible variables regardless of parents, weeds out common JS namespace variable. */
export const getAllIdentifiers = (formula: string, varNames?: string[]) => {
  const tokens = esprima.tokenize(formula);
  // let ignoreKeys: string[] = varNames || []; 
  let ignoreKeys = [...(varNames || []), ...new Set([Array, Date, String, Number, Boolean].flatMap(
    c => [c.toString().match(/\w+/g)![1], ...Object.getOwnPropertyNames(c.prototype)]
  ))];
  return [...new Set(tokens.filter((t, i, array) => t.type === 'Identifier' && ignoreKeys.indexOf(t.value) === -1).map(t => t.value))];
}

// This should really go in an aidkit-common package but we don't have that.
export function filterDict<T>(dict: Record<string, T>, filter: (key: string, value: T) => boolean): Record<string, T> {
  let filtered: typeof dict = {};
  for (let key of Object.keys(dict)) {
    if (filter(key, dict[key])) {
      filtered[key] = dict[key];
    }
  }
  return filtered;
}

export type AppsWithDetailInfo = Record<string, Record<string, string>>;

/**
 * limits the number of concurrent requests to a set number of slots.
 * Whenever a slot opens, the next request is started.
 */
export async function limitConcurrentRequests<T = any>(promises: (() => Promise<T>)[], slots: number): Promise<PromiseSettledResult<T>[]> {
  let remaining = slots;
  const completed: PromiseSettledResult<T>[] = Array(promises.length);
  return await new Promise((resolve) => {
    function queueNext() {
      const next = promises.pop();
      if (next) {
        const spot = promises.length;
        next()
          .then(
            (value) => completed[spot] = ({ value: value as T, status: 'fulfilled' }),
            (reason) => completed[spot] = ({ reason, status: 'rejected' }))
          .finally(() => queueNext());
      } else {
        remaining--;
      }

      if (remaining === 0) {
        resolve(completed);
      }
    }

    for (let i = 0; i < slots; i++) {
      queueNext();
    }
  })
}

export function extractResults<T>(results: PromiseSettledResult<T>[]): { successes: T[], failures: any[] } {
  const successes: T[] = [];
  const failures: any[] = [];
  for (const r of results) {
    switch (r.status) {
      case 'rejected':
        failures.push(r.reason);
        break;
      case 'fulfilled':
        successes.push(r.value);
        break;
    }
  }

  return { successes, failures };
}

/**
 * Counter-intuitively, (but intentionally!) if you exclude the info parameter, 
 * all conditions are considered true.
 * e.g. it will return the fully expanded analysis tree.
 *
 * @param info if null or undefined, all conditions resolve to true. if included, used in evaluating conditions
 */
export function flattenAnalyses(block: AIAnalysis | AIAnalysis[], info?: Record<string, string | undefined>): SingleLLMAnalysis[] {
  if (Array.isArray(block)) {
    return block.flatMap(b => flattenAnalyses(b, info));
  }
  switch (block.kind) {
    case 'LLM Analysis':
      return [block];
    case 'Conditional Analysis':
      if (!info || evalDistroConditional(block.shouldRun, info)) {
        return block.analyses.flatMap(a => flattenAnalyses(a, info));
      } else {
        return [];
      }
  }
}

export function safeParse(json: string, safeReturn?: any) {
  try {
    return JSON.parse(json);
  } catch (e) {
    if (typeof safeReturn !== 'undefined') return safeReturn;
    console.warn("Error with safe parse", e, json);
    return {};
  }
}

export function infoReplacer(match: string, props: { info: Record<string, string | undefined>, uid?: string }): string {
  const variable = match.slice(1).replace(/\\_/g, '_').replace(/[\\{\\}]/g, '');
  if (variable === "uid") return props.uid || '';

  let value: string | undefined | null = '';
  if (variable.includes('.')) {
    const [baseKey, ...path] = variable.split('.');
    const base = safeParse(props.info[baseKey]!, {});

    value = path.reduce((obj, next) => obj[next], base);

    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }
  } else {
    value = props.info[variable];
  }

  if (typeof value !== 'undefined' && value !== null) {
    return sanitizeDangerousText(value) || '';
  }

  return match;
}

